import { FunctionComponent, useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  LoginButton,
  MenuContainer,
  TextMenuItem,
  MobileAndCartWrapper,
  OpenedMenuContainer,
  VisibleWrapper,
} from "./styles";
import MenuItem from "./MenuItem";
import useLogoutModal from "./useLogoutModal";

import { cartState } from "state/cart-slice";
import { configState, setIsMenuOpen } from "state/config-slice";
import { userState, setMustSelectContract } from "state/user-slice"; // Import setChangeAccount
import ShoppingTrolley from "components/IconImage/shopping-trolley";
import BurgerMenu from "components/IconImage/burger-menu";
import Cross from "components/IconImage/cross";
import AnimatedContainer from "components/AnimatedContainer";
import useScreenDimensions from "hooks/useScreenDimensions";
import breakpoints from "themes/default/breakpoints";
import FeatureWrapper from "components/FeatureToggle/FeatureWrapper";

interface IMenuSelector {
  isCartVisible?: boolean;
  isLogButtonsVisible?: boolean;
  isLogoCentered: boolean;
}

const MenuSelector: FunctionComponent<IMenuSelector> = ({ isCartVisible, isLogButtonsVisible, isLogoCentered }) => {
  const currentCart = useSelector(cartState);
  const activePath = useLocation().pathname;
  const { t } = useTranslation();
  const userInfo = useSelector(userState);
  const config = useSelector(configState);
  const url = config.openidUrls?.find((el) => el.type === "bronze" || el.type === "gold")?.url;
  const { openLogoutModal } = useLogoutModal();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { width } = useScreenDimensions();

  useEffect(() => {
    if (config.isMenuOpen) dispatch(setIsMenuOpen(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath]);

  const onMenuClickHandle = useCallback(
    (callback: () => void) => {
      if (config.isMenuOpen) dispatch(setIsMenuOpen(false));
      callback();
    },
    [config.isMenuOpen, dispatch],
  );

  const myAccountBtn = useMemo(() => {
    return (
      isLogButtonsVisible &&
      userInfo.loggedIn && (
        <TextMenuItem
          data-testid="my-account-btn"
          title={t("My account")}
          onClick={() => onMenuClickHandle(() => navigate("/myaccount"))}
        />
      )
    );
  }, [isLogButtonsVisible, navigate, onMenuClickHandle, t, userInfo.loggedIn]);

  const logoutBtn = useMemo(() => {
    return (
      isLogButtonsVisible &&
      userInfo.loggedIn && (
        <TextMenuItem
          data-testid="log-out-btn"
          title={t("Log Out")}
          onClick={() => onMenuClickHandle(() => openLogoutModal())}
        />
      )
    );
  }, [isLogButtonsVisible, onMenuClickHandle, openLogoutModal, t, userInfo.loggedIn]);

  const accountSelectorBtn = useMemo(() => {
    return (
      isLogButtonsVisible &&
      userInfo.loggedIn && (
        <FeatureWrapper name="Change Account">
          <TextMenuItem
            data-testid="account-selector-btn"
            title={t("Change Account")}
            onClick={() => {
              dispatch(setMustSelectContract(true));
              onMenuClickHandle(() => navigate("/cable"));
            }}
          />
        </FeatureWrapper>
      )
    );
  }, [dispatch, isLogButtonsVisible, navigate, onMenuClickHandle, t, userInfo.loggedIn]);

  const loginBtn = useMemo(() => {
    return (
      isLogButtonsVisible &&
      !userInfo.loggedIn && (
        <LoginButton data-testid="log-in-btn" href={url}>
          {t("Log In")}
        </LoginButton>
      )
    );
  }, [isLogButtonsVisible, t, url, userInfo.loggedIn]);

  return (
    <MenuContainer fullExtend={isLogButtonsVisible || false} marginless={isLogoCentered}>
      {width >= breakpoints.values.lg && (
        <>
          {myAccountBtn}
          {accountSelectorBtn}
          {logoutBtn}
          {loginBtn}
        </>
      )}

      <MobileAndCartWrapper>
        {isCartVisible && (
          <VisibleWrapper visible={!config.isMenuOpen}>
            <MenuItem
              data-testid="basket-btn"
              icon={<ShoppingTrolley />}
              label="Basket"
              isActive={activePath === "/basket"}
              link={"/basket"}
              notificationsAmount={currentCart.cart.length}
            />
          </VisibleWrapper>
        )}
        {width < breakpoints.values.lg && isLogButtonsVisible && (
          <MenuItem
            data-testid="menu-btn"
            icon={config.isMenuOpen ? <Cross /> : <BurgerMenu />}
            label={config.isMenuOpen ? "Close" : "Menu"}
            aria-label={config.isMenuOpen ? "Close" : "Menu"}
            title={config.isMenuOpen ? "Close" : "Menu"}
            role="button"
            onClick={() => dispatch(setIsMenuOpen(!config.isMenuOpen))}
            link="#"
          />
        )}
      </MobileAndCartWrapper>

      <OpenedMenuContainer isVisibleBySliding={config.isMenuOpen}>
        {config.isMenuOpen && (
          <AnimatedContainer>
            <TextMenuItem
              data-testid="home-btn"
              title={t("Home")}
              onClick={() => onMenuClickHandle(() => navigate("/"))}
            />
            {myAccountBtn}
            {accountSelectorBtn}
            {logoutBtn}
            {loginBtn}
          </AnimatedContainer>
        )}
      </OpenedMenuContainer>
    </MenuContainer>
  );
};

export default MenuSelector;
